import { useSyncExternalStore } from 'react';

/**
 * External store for notistack's `domRoot`.
 *
 * Notistack renders snackbars inside the `SnackbarProvider`'s subtree by default.
 * When a descendant subtree enters native element fullscreen via `requestFullscreen`,
 * only that element (and its children) is shown on the fullscreen layer, so
 * snackbars rendered outside of it become invisible.
 *
 * To keep snackbars visible while a specific element is fullscreen, that element
 * publishes itself here; the top-level `SnackbarProvider` subscribes and forwards
 * the value as its `domRoot`, causing snackbars to be portaled into the fullscreen
 * subtree. When fullscreen exits, the value is cleared and snackbars fall back to
 * the default in-tree rendering.
 */

let current: HTMLElement | null = null;
const listeners = new Set<() => void>();

export function setSnackbarDomRoot(el: HTMLElement | null): void {
  if (current === el) return;
  current = el;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): HTMLElement | null {
  return current;
}

export function useSnackbarDomRoot(): HTMLElement | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
